import type { SectionType } from "../types";
import type { Block, SurfaceDepth, SurfaceGuardrail, SurfaceSpec, SurfaceTrigger } from "./surface-spec";
import { blockTypeOrigin, depthCap, GEMSALES_BLOCK_TYPES, SHARED_BLOCK_TYPES } from "./block-registry";
import type { Angle, BrandVoice, Objection } from "./advisory";
import { validateSurfaceSpec } from "./validate";
import type { Catalog, ValidationResult } from "./validate";

/**
 * The generation pipeline. Everything here is deterministic and testable; the
 * only non-deterministic step — the model call — is injected as a `SurfaceCompleter`
 * seam. The real Anthropic call plugs in later without changing this pipeline.
 *
 * The model only authors the variable part (blocks + a guardrail hint). GemSales
 * owns the deterministic envelope (surfaceId, cacheKey, store, trigger, intent,
 * style.packRef) so the model can never invent a tenant, a cache key, or a style.
 */

export interface GenerationContext {
  store: string;
  entry: SurfaceTrigger["entry"];
  query: string;
  /** Prior turns in this conversation (oldest→newest), so a follow-up builds on what was already shown. */
  history?: Array<{ query: string; summary: string }>;
  intent: { cluster: string; score: number };
  /** One-line read of the shopper's underlying need (from the query classifier) — a grounded hook seed. */
  needSummary?: string;
  depth: SurfaceDepth;
  sectionAnchor?: SectionType;
  source?: SurfaceTrigger["source"];
  product?: { ref: string; title: string; price?: number; image?: string };
  /** Enriched detail for the in-view product (from the catalog): substance the model grounds copy on. */
  productDetail?: { description?: string; tags?: string[]; productType?: string; variants?: Array<{ title: string; price?: number; available?: boolean }> };
  /** Catalog products ranked as the best fit for the shopper's need — the model may feature these, not just the page product. */
  productCandidates?: Array<{ ref: string; title: string; price?: number; productType?: string }>;
  /** Stable anonymous shopper id (cross-session) — carried for personalization + future memory. */
  anonymousId?: string;
  /** What we know about this returning shopper (from the cross-session profile store). */
  shopperProfile?: { visits: number; recentNeeds: string[]; recentProducts: string[] };
  /** What's already in the basket, so the surface can complete a routine/bundle rather than restart it. */
  cartSubtotal?: number;
  /** The specific line-items in the basket — so the surface completes THIS cart, not a generic upsell. */
  cartItems?: Array<{ title: string; qty?: number }>;
  stylePackRef: string;
  /** Restricts which block types the model may emit; defaults to the full registry. */
  allowedBlockTypes?: string[];
  /** Approved proofs the model may cite via binding.proofRef for factual claims. */
  availableProofs?: Array<{ ref: string; text: string; topic?: string }>;
  /** Advisory knowledge (kho tư vấn) that conditions copy: voice + angle + objections. */
  brandVoice?: BrandVoice;
  angle?: Angle;
  objections?: Objection[];
  /** Composition recipe: where in the funnel + the proven block order to suggest. */
  funnelStage?: string;
  recipeSkeleton?: string[];
  /** Surfaces on THIS store that actually converted for a similar question — proven exemplars (P3·2). */
  winExemplars?: Array<{ query: string; skeleton: string[]; headline?: string; count: number }>;
  /** Specific weaknesses from a prior attempt's grade — fed back on a quality-gate regeneration. */
  critiqueHint?: string[];
  generatedBy?: string;
  ttlSeconds?: number;
}

export interface CompletionRequest {
  system: string;
  prompt: string;
  /** JSON schema describing the expected output (documentation + test fixtures; NOT sent to the API). */
  schema: Record<string, unknown>;
  maxBlocks: number;
  /**
   * Structured-output schema. When present, the Anthropic completer sends it as
   * `output_config.format` (`json_schema`), so the API itself guarantees the JSON shape and the
   * completer returns the text untouched (no fence stripping). Must obey the structured-output
   * subset: `additionalProperties: false` on every object, no min/maxItems beyond 0/1, no
   * min/maxLength, ≤24 optional and ≤16 union params per request.
   *
   * The surface generation request does NOT set this: its `slots` are polymorphic per block type
   * (copy / options / rows / items / action / target, each with its own fields), which would blow
   * the optional/union limits, so generation keeps the prompt + validator + repair gate.
   */
  outputSchema?: Record<string, unknown>;
}

/** The model-call seam. Returns the raw JSON text the model produced. */
export type SurfaceCompleter = (request: CompletionRequest) => Promise<string>;

export interface GenerationResult {
  ok: boolean;
  spec: SurfaceSpec | null;
  validation: ValidationResult | null;
  /** Pipeline-level notes: parse errors, repairs applied, validation failures. */
  issues: string[];
  raw?: string;
}

const ALL_BLOCK_TYPES: string[] = [...SHARED_BLOCK_TYPES, ...GEMSALES_BLOCK_TYPES];

export async function generateSurface(
  ctx: GenerationContext,
  complete: SurfaceCompleter,
  catalog?: Catalog,
): Promise<GenerationResult> {
  const request = buildCompletionRequest(ctx);
  const issues: string[] = [];

  let raw: string;
  try {
    raw = await complete(request);
  } catch (error) {
    return { ok: false, spec: null, validation: null, issues: [`completer_failed: ${errorMessage(error)}`] };
  }

  const parsed = safeParse(raw);
  if (!parsed.ok) {
    return { ok: false, spec: null, validation: null, issues: [`parse_error: ${parsed.error}`], raw };
  }

  const modelBlocks = Array.isArray(parsed.value.blocks) ? (parsed.value.blocks as Block[]) : [];
  const spec = assembleSpec(ctx, modelBlocks, coerceGuardrail(parsed.value.guardrail));

  let validation = validateSurfaceSpec(spec, catalog);
  let finalSpec = spec;

  if (!validation.ok) {
    const repaired = repair(spec, validation);
    if (repaired.notes.length > 0) {
      finalSpec = repaired.spec;
      issues.push(...repaired.notes);
      validation = validateSurfaceSpec(finalSpec, catalog);
    }
  }

  if (!validation.ok) {
    issues.push(...validation.issues.filter((issue) => issue.severity === "error").map((issue) => `invalid: ${issue.code}`));
    return { ok: false, spec: null, validation, issues, raw };
  }

  return { ok: true, spec: finalSpec, validation, issues, raw };
}

/** Suggests a surface depth from the entry point and intent strength. */
export function recommendDepth(input: {
  entry: SurfaceTrigger["entry"];
  intentScore: number;
  cold?: boolean;
}): SurfaceDepth {
  if (input.cold) return input.intentScore >= 0.5 ? 4 : 3; // cold traffic → story / full landing
  if (input.entry === "chat_icon") return 1; // proactive shopper, a tight answer
  if (input.intentScore >= 0.7) return 2; // hot & specific → offer surface
  return input.intentScore >= 0.4 ? 2 : 1;
}

export function cacheKeyFor(ctx: GenerationContext): string {
  const product = ctx.product?.ref ?? "-";
  const campaign = ctx.source?.campaign ?? "-";
  // The QUERY must be part of the key. Without it, two different questions in the same
  // intent/product/campaign/depth bucket returned the byte-identical cached surface — the cache was
  // silently erasing personalization. We key on a normalized query (lower-cased, whitespace-collapsed,
  // stop-char-stripped) so the SAME question still shares a cached surface across shoppers, while a
  // DIFFERENT question gets its own generation.
  // A follow-up is thread-shaped: fold the prior questions into the key so the same standalone question
  // and a follow-up-to-the-same-question don't collide (and two identical threads still share a surface).
  const thread = ctx.history?.length
    ? `|h:${ctx.history.map((t) => normalizeQueryForCache(t.query)).join(">").slice(0, 80)}`
    : "";
  return `${ctx.intent.cluster}|${product}|${campaign}|d${ctx.depth}|q:${normalizeQueryForCache(ctx.query)}${thread}`;
}

/** Collapse a query to its cache-stable core so paraphrase-identical asks share a surface. */
export function normalizeQueryForCache(query: string): string {
  return (query ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

export function buildCompletionRequest(ctx: GenerationContext): CompletionRequest {
  const allowed = ctx.allowedBlockTypes ?? ALL_BLOCK_TYPES;
  const maxBlocks = depthCap(ctx.depth);
  return {
    system: buildSystemPrompt(allowed, maxBlocks, ctx.availableProofs ?? []),
    prompt: buildUserPrompt(ctx),
    schema: buildSurfaceSchema(allowed, maxBlocks),
    maxBlocks,
  };
}

/** The advisory block of the user prompt — brand voice, the chosen angle, objections. */
function buildAdvisoryBrief(ctx: GenerationContext): string {
  const parts: string[] = [];
  if (ctx.brandVoice) {
    const v = ctx.brandVoice;
    parts.push(
      `BRAND VOICE (${v.register}) — write every line in this voice:`,
      ...v.rules.map((r) => `  · ${r}`),
      `  · Prefer words: ${v.lexicon.prefer.join(", ")}. Avoid: ${v.lexicon.avoid.join(", ")}.`,
      `  · CTA personality: ${v.ctaPersonality}.`,
    );
  }
  if (ctx.angle) {
    parts.push(`ANGLE — lead with this pain and story direction (do not restate verbatim; make it vivid):`, `  · Pain: ${ctx.angle.painPoint}`, `  · Hook direction: ${ctx.angle.hook}`);
  }
  if (ctx.objections?.length) {
    parts.push(
      "OBJECTIONS — address these in an faq (phrase the answers in-voice; cite the proofRef when given):",
      ...ctx.objections.slice(0, 5).map((o) => `  · Q: ${o.q} → A: ${o.answer}${o.proofRef ? ` [cite ${o.proofRef}]` : ""}`),
    );
  }
  return parts.join("\n");
}

function buildSystemPrompt(allowed: string[], maxBlocks: number, proofs: Array<{ ref: string; text: string; topic?: string }>): string {
  // Claims must be backed by an approved proof. When the store has a proof library,
  // hand the model the exact refs it may cite — so a proof/comparison/metric block
  // carries a real binding.proofRef and validates "clean" instead of "needs_review".
  const claimRule = proofs.length
    ? [
        "- APPROVED PROOFS you may cite (use the exact ref in binding.proofRef):",
        ...proofs.slice(0, 12).map((p) => `    · ${p.ref}${p.topic ? ` (${p.topic})` : ""}: ${p.text.slice(0, 90)}`),
        "- For a factual/lab claim in proof/comparison/metric, cite EXACTLY ONE approved ref via binding.proofRef, and set guardrail.status='clean'. Never invent a ref. If no approved proof fits a claim, drop that claim (or set guardrail.status='needs_review').",
      ]
    : ["- You MAY state benefits in copy. For a hard factual/lab claim in proof/comparison/metric, set guardrail.status='needs_review' (do NOT invent a proofRef)."];
  return [
    "You author a GemSales commerce surface — a mini storytelling landing page — as an ordered list of blocks.",
    "Each block: { type, slots, binding? }. ALL content goes INSIDE slots. Shapes:",
    '- text blocks (answer/hook/agitate/summary/proof/guarantee/urgency): slots.copy = { headline?, title?, body?, sub?, label? }.',
    "- metric: slots.copy = { value, detail }. comparison: slots.rows = [{ label, value }]. faq: slots.items = [{ q, a }].",
    "- bundle: slots.options = [{ label, productRef, qty, featured?, discountPct? }]. cta: slots.action = 'add_to_cart', slots.copy.label.",
    "- a claim block cites a proof as binding: { proofRef: 'proof:...' } alongside its slots.",
    "Rules you must obey:",
    `- Use at most ${maxBlocks} blocks, only these types: ${allowed.join(", ")}.`,
    "- Open with a hook, tell a persuasive story (agitate → proof/metric/comparison → offer), close with a CTA.",
    "- Copy craft (make it read like a premium landing page, not a chatbot): concrete and sensory over generic — name the texture, the outcome, the number, the moment of use. Short confident sentences. One idea per block. Lead with the shopper's benefit, then the reason. Match the store's brand voice and the shopper's language. No filler, no hedging, no repetition across blocks.",
    "- Visuals: the opening hook/hero and any product block automatically show the store's REAL product photo — so lead with a hook or hero, and use a product block for the offer, to let imagery carry the page. Do NOT write any image/URL fields yourself; GemSales binds real images.",
    "- slots.copy.* is plain text only. Never write HTML.",
    ...claimRule,
    "- Reference products ONLY by a ref listed in context — the in-view product or a best-fit candidate. Feature whichever truly fits the shopper's need; never invent a ref, price, or SKU.",
    "- Do not output store, surfaceId, cacheKey, style, id, or origin — GemSales owns those.",
    // A concrete quality bar. One exemplar teaches the copy voice + block rhythm far better than adjectives:
    // specific sensory detail, the shopper's real moment, benefit-led lines, one idea per block, a clean close.
    "THE BAR — an example of the copy quality and block rhythm expected (adapt to THIS store/shopper; never copy the words):",
    '{"blocks":[{"type":"hook","slots":{"copy":{"headline":"The 2am ceiling stare, gone.","body":"You did everything right today. Your brain didn\'t get the memo."}}},{"type":"agitate","slots":{"copy":{"body":"Melatonin knocks you out, then leaves you foggy at 6am. You wanted rest, not a hangover."}}},{"type":"metric","slots":{"copy":{"value":"23 min","detail":"faster to sleep in the clinical study — without the morning grog"}},"binding":{"proofRef":"proof:sleep-onset"}},{"type":"bundle","slots":{"options":[{"label":"Nightly Wind-Down","productRef":"magnesium-glycinate","qty":1,"featured":true,"discountPct":15}]}},{"type":"guarantee","slots":{"copy":{"label":"Sleep on it for 30 nights — or your money back."}}},{"type":"cta","slots":{"action":"add_to_cart","copy":{"label":"Start sleeping tonight"}}}],"guardrail":{"status":"clean","claimsChecked":1}}',
    "Return JSON: { blocks: [{ type, slots, binding? }], guardrail: { status, claimsChecked } }.",
    "Output ONLY the raw JSON object — no markdown fences, no prose before or after.",
  ].join("\n");
}

function buildUserPrompt(ctx: GenerationContext): string {
  const lines = [
    `Store: ${ctx.store}`,
    ...(ctx.history?.length
      ? [
          "Conversation so far (oldest first) — this is a FOLLOW-UP; build on it, don't restart:",
          ...ctx.history.slice(-4).map((t, i) => `  ${i + 1}. Asked: "${t.query}" → showed: ${t.summary}`),
        ]
      : []),
    `Shopper question${ctx.history?.length ? " (latest)" : ""}: ${ctx.query}`,
    ...(ctx.needSummary ? [`Underlying need: ${ctx.needSummary}`] : []),
    ...(ctx.shopperProfile
      ? [
          `Returning shopper (visit ${ctx.shopperProfile.visits + 1}). Previously asked about: ${ctx.shopperProfile.recentNeeds.slice(0, 3).join("; ") || "—"}. Shown before: ${ctx.shopperProfile.recentProducts.slice(0, 4).join(", ") || "—"}. Acknowledge the return subtly and build on it — don't repeat what they've already seen.`,
        ]
      : []),
    `Intent: ${ctx.intent.cluster} (score ${ctx.intent.score.toFixed(2)})`,
    `Depth: ${ctx.depth} (answer=1, offer=2, story=3, landing=4)`,
    `Entry: ${ctx.entry}`,
  ];
  if (ctx.sectionAnchor) lines.push(`Page section: ${ctx.sectionAnchor}`);
  if (ctx.source?.angle) lines.push(`Traffic angle: ${ctx.source.angle}`);
  else if (ctx.source?.campaign) lines.push(`Arrived via campaign: ${ctx.source.campaign}`);
  if (ctx.cartItems?.length) {
    const items = ctx.cartItems.slice(0, 6).map((i) => `${i.title}${i.qty && i.qty > 1 ? ` ×${i.qty}` : ""}`).join(", ");
    lines.push(`Already in cart: ${items}${typeof ctx.cartSubtotal === "number" && ctx.cartSubtotal > 0 ? ` (subtotal ${ctx.cartSubtotal})` : ""}. COMPLETE this specific basket — a routine/bundle/add-on that pairs with what's there — not a fresh single-product pitch.`);
  } else if (typeof ctx.cartSubtotal === "number" && ctx.cartSubtotal > 0) {
    lines.push(`Shopper already has items in cart (subtotal ${ctx.cartSubtotal}). Lead toward COMPLETING the basket — a routine/bundle/add-on that finishes what they started — not a fresh single-product pitch.`);
  }
  if (ctx.product) {
    const price = ctx.product.price !== undefined ? ` at ${ctx.product.price}` : "";
    lines.push(`Product in view: ${ctx.product.title} (ref ${ctx.product.ref})${price}`);
    const d = ctx.productDetail;
    if (d?.description) lines.push(`  ↳ What it is: ${d.description.slice(0, 400)}`);
    if (d?.tags?.length) lines.push(`  ↳ Tags: ${d.tags.slice(0, 10).join(", ")}`);
    if (d?.variants?.length) {
      const opts = d.variants.slice(0, 5).map((v) => `${v.title}${v.price !== undefined ? ` (${v.price})` : ""}${v.available === false ? " — sold out" : ""}`).join(" · ");
      lines.push(`  ↳ Options (help the shopper pick the right tier/format): ${opts}`);
    }
    lines.push("Use the description/tags/options above to ground claims and copy in what this product ACTUALLY is — never invent attributes.");
  }
  if (ctx.productCandidates?.length) {
    lines.push(
      "",
      "BEST-FIT PRODUCTS for this need (ranked; feature the one(s) that truly answer the question — this may NOT be the product in view):",
      ...ctx.productCandidates.map((c) => `  · ${c.title} (ref ${c.ref})${c.price !== undefined ? ` at ${c.price}` : ""}${c.productType ? ` — ${c.productType}` : ""}`),
      "Choose the best fit for the shopper's actual need; combine into a routine/bundle when it makes sense. Use only these refs or the in-view product's ref.",
    );
  }
  // Proven winners outrank the generic stage recipe: these are surfaces that closed a sale on THIS
  // store for a question like this one, so lead with what the shop's own shoppers actually bought from.
  if (ctx.winExemplars?.length) {
    lines.push(
      "",
      "PROVEN WINNERS on this store — surfaces like these converted for similar questions:",
      ...ctx.winExemplars
        .slice(0, 3)
        .map(
          (w) =>
            `  ✓ "${w.query}" → ${w.skeleton.join(" → ")}${w.headline ? ` · opened with: "${w.headline}"` : ""}${w.count > 1 ? ` (converted ${w.count}×)` : ""}`,
        ),
      "Borrow what made them work — the structure, the angle, the specificity of the opening. Do NOT copy their words; write for THIS shopper's question.",
    );
  }
  if (ctx.recipeSkeleton?.length) {
    lines.push(
      "",
      `FUNNEL STAGE: ${ctx.funnelStage ?? "?"}. Suggested block order (proven for this stage): ${ctx.recipeSkeleton.join(" → ")}.`,
      "Follow this structure; you may trim or lightly reorder to serve the story and fit the depth.",
    );
  }
  const advisory = buildAdvisoryBrief(ctx);
  if (advisory) lines.push("", advisory);
  if (ctx.critiqueHint?.length) {
    lines.push(
      "",
      "YOUR PREVIOUS ATTEMPT graded weak. Fix EACH of these before returning — same story, materially stronger:",
      ...ctx.critiqueHint.slice(0, 6).map((c) => `  ✗ ${c}`),
    );
  }
  return lines.join("\n");
}

function buildSurfaceSchema(allowed: string[], maxBlocks: number): Record<string, unknown> {
  return {
    type: "object",
    additionalProperties: false,
    required: ["blocks"],
    properties: {
      blocks: {
        type: "array",
        minItems: 1,
        maxItems: maxBlocks,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["id", "type", "origin", "slots"],
          properties: {
            id: { type: "string" },
            type: { type: "string", enum: allowed },
            origin: { type: "string", enum: ["shared", "gemsales"] },
            binding: {
              type: "object",
              additionalProperties: false,
              properties: {
                products: { type: "array", items: { type: "string" } },
                collectionRef: { type: "string" },
                proofRef: { type: "string" },
              },
            },
            slots: { type: "object" },
          },
        },
      },
      guardrail: {
        type: "object",
        additionalProperties: false,
        required: ["status", "claimsChecked"],
        properties: {
          status: { type: "string", enum: ["clean", "needs_review"] },
          claimsChecked: { type: "boolean" },
        },
      },
    },
  };
}

/**
 * Normalize model-authored blocks before validation. `id` and `origin` are fully
 * derivable — id from position, origin from the type's registry entry — so we don't
 * trust the model to get them right (it routinely omits ids and mislabels origin).
 * This eliminates the missing_block_id / origin_mismatch failures at the source.
 */
const SLOT_KEYS = ["copy", "options", "rows", "items", "action", "target", "saveLabel"] as const;

function normalizeBlocks(blocks: Block[]): Block[] {
  return blocks.map((block, index) => {
    const raw = (block ?? {}) as unknown as Record<string, unknown>;
    const type = block?.type;
    const origin = blockTypeOrigin(type) ?? block?.origin;
    // The model often puts slot content (copy, options, rows…) at the block root
    // instead of under `slots`; the renderer reads `slots.*`, so hoist them in. Keeps
    // any it did nest correctly, and root wins only for keys slots doesn't already have.
    const slots: Record<string, unknown> = { ...(block?.slots ?? {}) };
    const rest: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (key === "slots") continue;
      if ((SLOT_KEYS as readonly string[]).includes(key)) {
        if (!(key in slots)) slots[key] = value;
      } else {
        rest[key] = value;
      }
    }
    return {
      ...(rest as object),
      id: block?.id && String(block.id).trim() ? block.id : `${type ?? "block"}-${index + 1}`,
      ...(origin ? { origin } : {}),
      slots,
    } as Block;
  });
}

function assembleSpec(ctx: GenerationContext, blocks: Block[], guardrail: SurfaceGuardrail): SurfaceSpec {
  const cacheKey = cacheKeyFor(ctx);
  const trigger: SurfaceTrigger = {
    entry: ctx.entry,
    ...(ctx.query ? { query: ctx.query } : {}),
    ...(ctx.sectionAnchor ? { sectionAnchor: ctx.sectionAnchor } : {}),
    ...(ctx.source ? { source: ctx.source } : {}),
  };

  return {
    version: "1.0",
    surfaceId: `srf_${fnv1a(`${cacheKey}|${ctx.query}`)}`,
    store: ctx.store,
    trigger,
    intent: { cluster: ctx.intent.cluster, score: ctx.intent.score, depth: ctx.depth },
    style: { packRef: ctx.stylePackRef },
    blocks: normalizeBlocks(blocks),
    guardrail,
    meta: {
      generatedBy: ctx.generatedBy ?? "llm",
      cacheKey,
      ttl: ctx.ttlSeconds ?? 86_400,
    },
  };
}

/** Deterministic, conservative repairs for the two safe failure modes. */
function repair(spec: SurfaceSpec, validation: ValidationResult): { spec: SurfaceSpec; notes: string[] } {
  const codes = new Set(validation.issues.filter((issue) => issue.severity === "error").map((issue) => issue.code));
  const notes: string[] = [];
  let next = spec;

  if (codes.has("depth_overflow")) {
    const cap = depthCap(spec.intent.depth);
    next = { ...next, blocks: next.blocks.slice(0, cap) };
    notes.push(`repaired: truncated to ${cap} blocks (depth ${spec.intent.depth})`);
  }

  // The model invented proofRefs not in the approved library — strip them so the
  // block carries no unverified citation, then let the claim→needs_review path own it.
  if (codes.has("unknown_proof_ref")) {
    next = {
      ...next,
      blocks: next.blocks.map((block) => {
        if (!block.binding?.proofRef) return block;
        const { proofRef, ...restBinding } = block.binding;
        return { ...block, binding: restBinding };
      }),
      guardrail: { status: "needs_review", claimsChecked: false },
    };
    notes.push("repaired: dropped unknown proofRef(s), guardrail → needs_review");
  }

  if (codes.has("claim_without_proof")) {
    next = { ...next, guardrail: { status: "needs_review", claimsChecked: next.guardrail.claimsChecked } };
    notes.push("repaired: guardrail downgraded to needs_review (unproven claim)");
  }

  return { spec: next, notes };
}

function coerceGuardrail(value: unknown): SurfaceGuardrail {
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const status = record.status === "clean" ? "clean" : "needs_review";
    return { status, claimsChecked: record.claimsChecked === true };
  }
  return { status: "needs_review", claimsChecked: false };
}

function safeParse(raw: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return { ok: false, error: "expected a JSON object" };
    }
    return { ok: true, value: value as Record<string, unknown> };
  } catch (error) {
    return { ok: false, error: errorMessage(error) };
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
